import { useNavigate } from "react-router-dom";
import { MOCK_USERS, MOCK_CARDS, MOCK_SESSIONS, MOCK_CONVERSATIONS } from "../lib/mockData";
import { ArrowLeft, Users, Tag, Calendar, MessageSquare, AlertTriangle, CheckCircle, Eye, Ban, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useT } from "../lib/useLanguage";
import { toast } from "sonner";
import { useState } from "react";

const MOCK_REPORTS = [
  { id: "r1", reporter: "Léa", target: "Utilisateur inconnu", reason: "Sollicitation romantique", status: "pending", date: "2026-05-25" },
  { id: "r2", reporter: "Yassine", target: "Utilisateur Y", reason: "Spam — messages répétés", status: "pending", date: "2026-05-24" },
  { id: "r3", reporter: "Camille", target: "Utilisateur Z", reason: "Profil suspect", status: "reviewed", date: "2026-05-23" },
];

const POPULAR_TAGS = ["Python", "CV", "Anglais", "Running", "Excel", "Guitare", "LinkedIn", "Maths", "Canva", "Figma"];
const POPULAR_ZONES = ["Campus Talence", "Peixotto", "En ligne", "Forum", "Talence Centre", "KEDGE"];

export default function Admin() {
  const navigate = useNavigate();
  const t = useT();
  const [hiddenUsers, setHiddenUsers] = useState([]);

  const stats = [
    { icon: Users, label: "Profils", value: MOCK_USERS.length, color: "#FF6B4A" },
    { icon: Tag, label: "Cartes", value: MOCK_CARDS.length, color: "#B9F85A" },
    { icon: Calendar, label: "Sessions", value: MOCK_SESSIONS.length, color: "#A7D8FF" },
    { icon: MessageSquare, label: "Messages", value: MOCK_CONVERSATIONS.length, color: "#5B4BFF" },
    { icon: AlertTriangle, label: "Signalements", value: MOCK_REPORTS.filter(r => r.status === "pending").length, color: "#FF6B4A" },
    { icon: TrendingUp, label: "Échanges actifs", value: MOCK_CONVERSATIONS.filter(c => c.tab === "active").length, color: "#B9F85A" },
  ];

  return (
    <div className="max-w-lg mx-auto px-4 pt-4 pb-8">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground mb-5 hover:text-foreground">
        <ArrowLeft className="h-4 w-4" />Retour
      </button>
      <h1 className="text-xl font-extrabold mb-5">{t("admin_title")}</h1>

      {/* Stats grid */}
      <div className="grid grid-cols-3 gap-2 mb-6">
        {stats.map((s, i) => (
          <div key={i} className="bg-card rounded-xl border border-border p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <s.icon className="h-3.5 w-3.5" style={{ color: s.color }} />
              <span className="text-xs text-muted-foreground">{s.label}</span>
            </div>
            <p className="text-2xl font-extrabold" style={{ color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Reports */}
      <h2 className="text-sm font-bold mb-3 flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-destructive" />Signalements
      </h2>
      <div className="space-y-2 mb-6">
        {MOCK_REPORTS.map(r => (
          <div key={r.id} className="bg-card rounded-xl border border-border p-3.5">
            <div className="flex justify-between items-start mb-1.5">
              <div>
                <span className="text-sm font-medium">{r.reporter} → {r.target}</span>
                <p className="text-xs text-muted-foreground">{r.reason}</p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${r.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>
                {r.status === "pending" ? "En attente" : "Traité"}
              </span>
            </div>
            <div className="flex gap-2 mt-2">
              <Button size="sm" variant="outline" className="rounded-full text-xs flex-1" onClick={() => toast.success("Signalement ignoré.")}>Ignorer</Button>
              <Button size="sm" className="rounded-full text-xs flex-1" style={{ backgroundColor: "#FF6B4A" }} onClick={() => toast.success("Utilisateur suspendu.")}>Suspendre</Button>
              <Button size="sm" variant="destructive" className="rounded-full text-xs" onClick={() => toast.success("Utilisateur banni.")}>Bannir</Button>
            </div>
          </div>
        ))}
      </div>

      {/* Popular tags */}
      <h2 className="text-sm font-bold mb-2">Tags populaires</h2>
      <div className="flex flex-wrap gap-1.5 mb-5">
        {POPULAR_TAGS.map((tag, i) => (
          <span key={tag} className="text-xs px-2.5 py-1 rounded-full font-medium"
            style={{ backgroundColor: i < 3 ? "#B9F85A" : "#FF6B4A15", color: i < 3 ? "#101828" : "#FF6B4A" }}>
            {tag}
          </span>
        ))}
      </div>

      {/* Users list */}
      <h2 className="text-sm font-bold mb-3">Utilisateurs récents</h2>
      <div className="space-y-2">
        {MOCK_USERS.slice(0, 10).map(u => (
          <div key={u.id} className={`flex items-center gap-3 bg-card rounded-xl border border-border p-3 transition-opacity ${hiddenUsers.includes(u.id) ? "opacity-40" : ""}`}>
            <img src={u.photo} alt="" className="h-9 w-9 rounded-xl object-cover" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{u.firstName} · {u.ageRange} · {u.status}</p>
              <p className="text-xs text-muted-foreground truncate">{u.zone} — {u.skillsOffered.slice(0, 2).join(", ")}</p>
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" className="p-1.5 h-auto" onClick={() => { setHiddenUsers(p => p.includes(u.id) ? p.filter(x => x !== u.id) : [...p, u.id]); }}>
                <Eye className="h-3.5 w-3.5 text-muted-foreground" />
              </Button>
              <Button size="sm" variant="ghost" className="p-1.5 h-auto" onClick={() => toast.success("Banni.")}><Ban className="h-3.5 w-3.5 text-red-400" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}